// ============================================================
// shared/consigneMachines.js — ce que la consigne INTERDIT comme machine
// ------------------------------------------------------------
// Devis 2026-10-015, 15 octobre 2026. Thibaud : « je lui demande
// uniquement fraisage et il s'obstine à faire du tournage ». Trois
// estimations de suite sur le NLX — et à la troisième, l'agent a écrit :
// « la consigne dit "uniquement en fraisage" mais cette directive semble
// destinée à d'autres pièces ».
//
// Le prompt donnait déjà l'autorité à la consigne. Ce n'était qu'une
// INTENTION : face à une pièce annoncée « rotative Ø49 » et à une machine
// présentée comme « la machine clé du parc pour une pièce rotative », le
// modèle a tranché contre le deviseur. Ce qui dépend de la bonne volonté
// du modèle n'est pas une règle.
//
// Exclure une famille de machines est une suite de mots dans une phrase :
// c'est CALCULABLE, donc en code (§2bis, règle 1), comme le brut de la
// consigne (`consigneBrut.js`). L'appelant retire alors les machines
// exclues du parc que l'agent reçoit — il ne peut plus les choisir — et
// vérifie la gamme rendue.
//
// Ce qu'il ne fait PAS : deviner. « Reprise sur 3 axes pour les
// méplats » NOMME une fraiseuse sans exclure le tour — la prise 1 d'une
// pièce tournée n'a pas besoin d'être écrite. Seules l'EXCLUSIVITÉ
// (« uniquement », « seulement », « que du », « tout en »), la NÉGATION
// (« pas de tournage », « sans tour ») et l'usinage ENTIER sur une
// machine (« usinage sur machine 3 axes ») excluent quelque chose.
// ============================================================

/** Les deux familles d'usinage qu'une consigne peut opposer. */
export const FAMILLES_USINAGE = {
  tournage: ['tour_cn', 'tour_fraiseur', 'tour_conventionnel'],
  fraisage: ['centre_3axes', 'centre_4axes', 'centre_5axes'],
};

const LIBELLE = { tournage: 'le tournage', fraisage: 'le fraisage' };

// Les mots qui désignent chaque famille. Les GÉNÉRIQUES servent à la
// négation : « pas de 5 axes » écarte un centre, pas tout le fraisage, et
// « sans le CTX » un tour, pas le NLX.
const GENERIQUES = {
  tournage: String.raw`(?:tournage|tourner|tours?)`,
  fraisage: String.raw`(?:fraisage|fraiser|fraiseuse)`,
};
const MOTS = {
  tournage: String.raw`(?:tournage|tourner|tours?(?:\s+cn)?|ctx(?:\s*\d+)?|nlx(?:\s*\d+)?|multus)`,
  fraisage: String.raw`(?:fraisage|fraiser|fraiseuse|centres?(?:\s+d['’]usinage)?|(?:machine\s+|centre\s+)?\d\s*axes?|hedelius)`,
};

// Les petits mots entre l'exclusivité et la machine : « que SUR LE 3 axes ».
const LIAISON = String.raw`(?:\s+(?:en|du|de|au|à|a|sur|par|le|la|les|une?|des|d['’]|machine))*\s+`;
const NEGATION = String.raw`(?:pas|sans|aucun|jamais|ni|interdit|exclu)`;

/** Les motifs d'EXCLUSIVITÉ d'une famille — ils excluent l'autre. */
function motifs(famille) {
  const m = MOTS[famille];
  return [
    // « uniquement en fraisage », « seulement sur le 3 axes »
    new RegExp(String.raw`\b(?:uniquement|seulement|exclusivement|rien\s+que)${LIAISON}${m}\b`, 'i'),
    // « que du fraisage », « que sur le NLX » — « que » seul est trop
    // courant (« il faut que la machine… ») pour suffire.
    new RegExp(String.raw`\bque\s+(?:du|en|au|sur)${LIAISON}?${m}\b`, 'i'),
    // « tout en fraisage », « tout au tour » — pas « tout le tour », qui
    // est le POURTOUR d'une pièce.
    new RegExp(String.raw`\btout\s+(?:en|au|sur)${LIAISON}?${m}\b`, 'i'),
    // « fraisage uniquement », « au tour seulement »
    new RegExp(String.raw`\b${m}\s+(?:uniquement|seulement|exclusivement)\b`, 'i'),
    // « usinage sur machine 3 axes », « usinage complet au tour »
    new RegExp(String.raw`\businage(?:\s+(?:complet|entier|total))?\s+(?:en|sur|au|à|a|par)${LIAISON}?${m}\b`, 'i'),
  ];
}

/** « pas de tournage », « sans fraisage » : la famille nommée est exclue. */
function nie(famille) {
  return new RegExp(String.raw`\b${NEGATION}${LIAISON}${GENERIQUES[famille]}\b`, 'i');
}

const autre = (f) => (f === 'tournage' ? 'fraisage' : 'tournage');

/**
 * Les familles de machines que la consigne EXCLUT. Pure.
 *
 * @param {string} texte la consigne du deviseur (description et/ou correction)
 * @returns {{ exclues: string[], types: string[], raisons: string[] }}
 *   `exclues` : 'tournage' | 'fraisage' ; `types` : les types de machine
 *   correspondants ; `raisons` : le fragment qui a décidé, pour l'écran.
 */
export function exclusionsDeConsigne(texte) {
  const t = String(texte ?? '');
  const exclues = new Map();
  for (const famille of ['tournage', 'fraisage']) {
    const n = t.match(nie(famille));
    if (n) exclues.set(famille, n[0].trim());
    for (const re of motifs(famille)) {
      const m = t.match(re);
      // « pas que du fraisage » n'est pas une exclusivité.
      if (m && !new RegExp(String.raw`\b(?:pas|non)\s+${m[0].trim().split(/\s+/)[0]}\b`, 'i').test(t)) {
        if (!exclues.has(autre(famille))) exclues.set(autre(famille), m[0].trim());
        break;
      }
    }
  }
  // Les deux familles exclues à la fois : la phrase se contredit (ou on
  // l'a mal lue). On n'exclut RIEN plutôt que de vider le parc.
  if (exclues.size === 2) return { exclues: [], types: [], raisons: [] };
  const liste = [...exclues.keys()];
  return {
    exclues: liste,
    types: liste.flatMap((f) => FAMILLES_USINAGE[f]),
    raisons: liste.map((f) => `${LIBELLE[f]} est exclu par la consigne (« ${exclues.get(f)} »)`),
  };
}

/**
 * La consigne d'une pièce, telle qu'elle s'applique : la CORRECTION la
 * plus récente l'emporte sur la description pour une famille qu'elle
 * tranche ; sinon la description vaut.
 */
export function exclusionsCombinees(description, correction) {
  const c = exclusionsDeConsigne(correction);
  if (c.exclues.length) return c;
  return exclusionsDeConsigne(description);
}

/**
 * Les lignes d'une gamme posées sur une machine que la consigne exclut.
 * Pure — l'appelant décide quoi en faire (le dire, jamais le corriger).
 *
 * @param {Array} gamme lignes avec `machine_id`
 * @param {Array} parc  machines avec `id`, `name`, `type`
 * @param {{types: string[]}} exclusions
 */
export function lignesHorsConsigne(gamme, parc, exclusions) {
  const interdits = new Set(exclusions?.types ?? []);
  if (!interdits.size) return [];
  const parId = new Map((parc ?? []).map((m) => [m.id, m]));
  return (gamme ?? []).filter((l) => interdits.has(parId.get(l.machine_id)?.type))
    .map((l) => ({ op_num: l.op_num, machine: parId.get(l.machine_id)?.name ?? String(l.machine_id) }));
}

// ============================================================
// LES MACHINES QUE LA CONSIGNE NOMME — 27 octobre 2026
// ------------------------------------------------------------
// Philippe, devis 2026-10-036 : « usinage sur ctx520 reprise pour faire
// les 3 chanfreins à la main ». La CTX 520 existe au parc, en deux
// exemplaires. Mais un nom qui n'y est pas — « CTX 600 », une faute de
// frappe, une machine vendue — passait par le motif générique `ctx\d+` :
// le fraisage était exclu, l'agent prenait un tour quelconque, et RIEN ne
// le disait. Un nom de machine est une suite de caractères comparée à une
// liste : c'est du code, et l'absence se dit, elle ne se devine pas.
// ============================================================

// Les marques et modèles du parc tels qu'un deviseur les écrit. « 3 axes »
// n'y est pas : c'est une famille, `exclusionsDeConsigne` s'en charge.
const NOM_MACHINE = /\b(ctx|nlx|multus|acura|hedelius|okuma|dmg(?:\s*mori)?)(?:\s*-?\s*(\d{2,4}))?\b/gi;
const compact = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * @param {string} texte la consigne
 * @param {Array<{name: string}>} parc
 * @returns {{ connues: {texte: string, machines: string[]}[], inconnues: string[] }}
 */
export function machinesNommees(texte, parc = []) {
  const connues = [];
  const inconnues = [];
  const vus = new Set();
  for (const m of String(texte ?? '').matchAll(NOM_MACHINE)) {
    const marque = compact(m[1]);
    const clef = `${marque}${m[2] ?? ''}`;
    if (vus.has(clef)) continue;
    vus.add(clef);
    const texteLu = m[0].trim();
    const machines = parc.filter((x) => compact(x.name).includes(clef)).map((x) => x.name);
    if (machines.length) connues.push({ texte: texteLu, machines });
    else inconnues.push(texteLu);
  }
  return { connues, inconnues };
}

// ============================================================
// UNE OPÉRATION « À LA MAIN » N'EST PAS UNE PRISE SUR UNE MACHINE CN
// ------------------------------------------------------------
// Même consigne. La gamme a posé les « 3 chanfreins à la main » DANS une
// deuxième prise sur la CTX 520 — retournement, 30 min de mise en route,
// 5 min par pièce sur le tour. Le deviseur décrit un travail d'établi
// après le tour ; la gamme le facture comme une prise de plus.
//
// On ne CORRIGE pas la gamme (une ligne déplacée changerait un prix sans
// que personne l'ait décidé) : on le DIT, comme une machine exclue.
// ============================================================

// `\b` ne connaît pas « à » (hors `[A-Za-z0-9_]`) : les bornes sont des lettres Unicode.
const MANUEL = /(?<!\p{L})(?:[àa]\s+la\s+main|manuel(?:le)?s?(?:ment)?)(?!\p{L})/iu;
const TYPES_CN = /^(?:tour_cn|tour_fraiseur|centre_)/;

/**
 * La consigne demande-t-elle un travail à la main que la gamme n'a pas
 * sorti des machines à commande numérique ? Pure.
 *
 * @returns {string|null} le fragment de la consigne, ou `null` si tout va bien
 */
export function travailManuelAbsent(texte, gamme = [], parc = []) {
  const m = String(texte ?? '').match(MANUEL);
  if (!m) return null;
  const parId = new Map(parc.map((x) => [x.id, x]));
  // Une ligne sur le POSTE MANUEL est à la main par construction, quel que
  // soit son libellé (« Chanfreinage », « Ébavurage »).
  const horsCN = (gamme ?? []).some((l) => {
    if (l.categorie === 'controle') return false;
    const type = parId.get(l.machine_id)?.type ?? '';
    if (type === 'poste_manuel') return true;
    return !TYPES_CN.test(type) && MANUEL.test(`${l.libelle ?? ''} ${l.detail ?? ''}`);
  });
  return horsCN ? null : m[0];
}
